import React, { useCallback, useRef, useState } from "react"
import { Table, Pagination, PaginationItem, PaginationLink, Input } from "reactstrap"
import DataTableLoader from "SkeletonLoader/DataTableLoader"

const CommonTable = ({
  loading,
  data,
  columns,
  page,
  totalPages,
  limit,
  setPage,
  selectable,
  selectedItems = [],
  onSelectItem,
  onSelectAll,
  showPagination = true,
  onSearch,
  searchPlaceholder = "Search...",
}) => {
  const allSelected = data?.length > 0 && selectedItems.length === data.length
  const [searchQuery, setSearchQuery] = useState("")
  const searchTimeoutRef = useRef(null)

  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(query)
      }
    }, 800) 
  }, [onSearch])

  const handleSearch = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    debouncedSearch(value)
  }

  return (
    <>
      {onSearch && (
        <div className="mb-3">
          <Input
            type="text"
            placeholder={searchPlaceholder}
            className="form-control"
            style={{ width: "300px" }}
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
      )}
      <div className="configurable-data-table table-responsive mt-4 mb-3">
        {loading ? (
          <DataTableLoader cols={columns.length} />
        ) : (
          <Table className="table mb-0">
            <thead className="table-light">
              <tr>
                {selectable && (
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onSelectAll}
                    />
                  </th>
                )}
                {columns.map((col, idx) => (
                  <th key={idx}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item._id || index}>
                  {selectable && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item._id)}
                        onChange={() => onSelectItem(item._id)}
                      />
                    </td>
                  )}
                  {columns.map((col, idx) => (
                    <td key={idx}>
                      {col.render ? col.render(item, index) : item[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {showPagination && (
        <div className="pagination-container d-flex align-items-center justify-content-end">
          <Pagination>
            <PaginationItem disabled={page <= 1}>
              <PaginationLink previous onClick={() => setPage(page - 1)} />
            </PaginationItem>

            {[...Array(Math.min(3, totalPages))].map((_, index) => (
              <PaginationItem key={index} active={index + 1 === page}>
                <PaginationLink onClick={() => setPage(index + 1)}>
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}

            {totalPages > 6 && page > 3 && page < totalPages - 2 && (
              <PaginationItem disabled>
                <PaginationLink>{"..."}</PaginationLink>
              </PaginationItem>
            )}

            {totalPages > 6 && page > 3 && page < totalPages - 2 && (
              <PaginationItem active>
                <PaginationLink onClick={() => setPage(page)}>
                  {page}
                </PaginationLink>
              </PaginationItem>
            )}

            {totalPages > 6 && page < totalPages - 2 && (
              <PaginationItem disabled>
                <PaginationLink>{"..."}</PaginationLink>
              </PaginationItem>
            )}

            {totalPages > 3 &&
              [...Array(Math.min(3, totalPages))].map((_, index) => {
                const pageNumber = totalPages - 2 + index;
                return (
                  <PaginationItem
                    key={pageNumber}
                    active={pageNumber === page}
                  >
                    <PaginationLink onClick={() => setPage(pageNumber)}>
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

            <PaginationItem disabled={page >= totalPages}>
              <PaginationLink next onClick={() => setPage(page + 1)} />
            </PaginationItem>
          </Pagination>
        </div>
      )}
    </>
  )
}

export default CommonTable
